import { css } from '@styled-system/css';
import { ArrowRight, FolderPlus, Search, Star } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAnalysisMenuListData } from '@/features/analysisMenu/hooks/useAnalysisMenuListData';
import type { AnalysisItemData } from '@/features/analysisMenu/types';
import { AnalysisEngineBanner } from '@/shared/components/analysis/AnalysisEngineBanner';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { Button } from '@/shared/components/ui/Button';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { SearchBarNew } from '@/shared/components/ui/SearchBarNew';
import { Spinner } from '@/shared/components/ui/Spinner';
import {
  useCreateAnalysisFromCriteria,
  useCreateVestiaireAnalysis,
  useRenameAnalysis,
} from '@/shared/hooks/analysis/mutations';

import { MOCKS_ENABLED } from '@/mocks/graphqlMockHandlers';

import {
  useSearchOrganizationServer,
  type SearchFolder,
} from '../organization/useSearchOrganizationServer';
import { FolderFormModal } from './FolderFormModal';
import { RenameSearchModal } from './RenameSearchModal';
import { SearchHistoryFolders } from './SearchHistoryFolders';
import { SearchModal, type SearchModalInput } from './SearchModal';

// ============================================================================
// Styles
// ============================================================================

const page = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '10',
  width: '100%',
  paddingTop: { base: '4', md: '8' },
});

// ─── Hero spotlight (proposition C) — neutre, mise en avant par l'échelle ──
// Pas de teinte, pas de carte : un très grand champ élevé, centré, porté par
// l'ombre et le blanc tournant. La couleur n'apparaît que sur le CTA.
const heroWrap = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '7',
  paddingY: { base: '6', md: '12' },
  marginBottom: { base: '4', md: '10' },
});

const heroTitle = css({
  fontSize: 'h1',
  lineHeight: 'h1',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.8px',
});

// Fake input — grand champ élevé 60px. The real form lives in the
// SearchModal; this is just the entry point.
const searchTrigger = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  width: '100%',
  maxWidth: '720px',
  paddingLeft: '7',
  paddingRight: '5',
  height: '60px',
  bg: 'surface.elevated',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'xl',
  color: 'text.tertiary',
  fontSize: 'body',
  fontFamily: 'sans',
  cursor: 'pointer',
  textAlign: 'left',
  boxShadow: 'lg',
  transition: 'border-color 120ms ease, box-shadow 160ms ease, transform 160ms ease',
  _hover: {
    borderColor: 'border.strong',
    boxShadow: 'xl',
    transform: 'translateY(-1px)',
  },
});

const searchTriggerText = css({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});


const heroHint = css({
  fontSize: 'caption',
  color: 'text.tertiary',
});

const historySection = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
});

const historyHeader = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const historyTitle = css({
  fontSize: 'micro',
  color: 'text.tertiary',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  fontWeight: 'medium',
});

const emptyState = css({
  paddingY: '16',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '3',
  color: 'text.tertiary',
  fontSize: 'bodySm',
  textAlign: 'center',
});

const centered = css({
  display: 'flex',
  justifyContent: 'center',
  paddingY: '16',
});

// ============================================================================
// Page
// ============================================================================

export const ProductSearchPage: FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createInFolderId, setCreateInFolderId] = useState<string | null>(null);
  const [folderModalMode, setFolderModalMode] = useState<'create' | 'rename' | null>(null);
  const [folderToEdit, setFolderToEdit] = useState<SearchFolder | null>(null);
  const [filterText, setFilterText] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [renameTarget, setRenameTarget] = useState<AnalysisItemData | null>(null);
  const [viewTarget, setViewTarget] = useState<AnalysisItemData | null>(null);
  const { renameAnalysis } = useRenameAnalysis();

  const { analyses, loading, error } = useAnalysisMenuListData('product');
  const { createAnalysisFromCriteria } = useCreateAnalysisFromCriteria();
  const { createVestiaireAnalysis } = useCreateVestiaireAnalysis();
  const { folders, actions } = useSearchOrganizationServer('catalog');

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR');
  };

  const buildMeta = (analysis: AnalysisItemData): string => {
    const metaParts: string[] = [];
    if (analysis.createdAt) metaParts.push(t('productSearch.createdOn', { date: formatDate(analysis.createdAt) }));
    // Création asynchrone : le compteur n'existe qu'une fois le calcul fini
    if (analysis.computeState === 'queued' || analysis.computeState === 'running') {
      metaParts.push(t('productSearch.computing'));
    } else if (analysis.computeState === 'failed') {
      metaParts.push(t('productSearch.computeFailed'));
    } else if (analysis.analysedProductCount) {
      metaParts.push(t('productSearch.productsAnalyzed', { count: analysis.analysedProductCount }));
    }
    return metaParts.join(' · ');
  };

  const handleSubmit = async (input: SearchModalInput) => {
    // Quota atteint : l'erreur QUOTA_ERROR_MESSAGE remonte telle quelle, SearchModal ouvre la popup.
    const result =
      input.platform === 'vestiaire'
        ? input.vestiaireCriteria
          ? await createVestiaireAnalysis({ title: input.title, criteria: input.vestiaireCriteria })
          : null
        : input.criteria
          ? await createAnalysisFromCriteria({ title: input.title, criteria: input.criteria })
          : null;
    if (!result) throw new Error(t('productSearch.createError'));
    if (result.success && result.analysis) {
      // Dossier choisi dans la popup + épinglage EN TÊTE de sa scope (le back
      // place les nouvelles recherches en fin de dossier par défaut)
      void actions.moveSearch(result.analysis.id, input.folderId ?? null, 0);
      navigate(`/analysis/products/${result.analysis.id}`);
    } else {
      throw new Error(result.error ?? t('productSearch.createError'));
    }
  };

  const handleFolderSubmit = (name: string) => {
    if (folderModalMode === 'rename' && folderToEdit) {
      void actions.renameFolder(folderToEdit.id, name);
    } else {
      void actions.createFolder(name);
    }
  };

  return (
    <AppLayout
      title={t('productSearch.title')}
      headerStats={
        <>
          {!loading && analyses.length > 0 && (
            <span>{t('productSearch.totalSearches', { count: analyses.length })}</span>
          )}
        </>
      }
    >
      <div className={page}>
        {/* État du moteur d'analyses — rend `null` quand tout va bien.
            Ici plutôt qu'ailleurs : c'est l'écran depuis lequel on LANCE un
            calcul, donc le seul moment où l'information est actionnable. */}
        <AnalysisEngineBanner />

        {/* ─── Hero spotlight — grand champ neutre, centré ─────────────── */}
        <div className={heroWrap}>
          <h1 className={heroTitle}>{t('productSearch.heroTitle')}</h1>
          {/* div cliquable (pas un <button>) : le CTA est un vrai Button DS */}
          <div
            role="button"
            tabIndex={0}
            className={searchTrigger}
            onClick={() => setIsModalOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsModalOpen(true);
              }
            }}
          >
            <Search size={18} strokeWidth={1.75} />
            <span className={searchTriggerText}>{t('productSearch.searchPlaceholder')}</span>
            {/* CTA DS — même bouton que « Aller dans la feed → » d'Autocop */}
            <Button
              variant="primary"
              size="md"
              trailingIcon={<ArrowRight size={14} strokeWidth={1.75} />}
              onClick={() => setIsModalOpen(true)}
            >
              {t('productSearch.newSearch')}
            </Button>
          </div>
          <span className={heroHint}>{t('productSearch.heroHint')}</span>
        </div>

        {/* ─── History organisé en dossiers ────────────────────────────── */}
        <div className={historySection}>
          <div className={historyHeader}>
            <span className={historyTitle}>
              {t('productSearch.history')}
              {MOCKS_ENABLED && (
                <span className={css({ marginLeft: '3', color: 'warning.default', textTransform: 'none' })}>
                  {t('productSearch.demo')}
                </span>
              )}
            </span>
          </div>

          {/* Toolbar : recherche + filtre favoris + nouveau dossier */}
          <div className={css({ display: 'flex', alignItems: 'center', gap: '3', flexWrap: 'wrap' })}>
            <SearchBarNew
              placeholder={t('folders.filterPlaceholder')}
              value={filterText}
              onChange={setFilterText}
            />
            <ChipToggle selected={favoritesOnly} onChange={setFavoritesOnly}>
              <span className={css({ display: 'inline-flex', alignItems: 'center', gap: '2' })}>
                <Star size={12} strokeWidth={1.75} fill={favoritesOnly ? 'currentColor' : 'none'} />
                {t('folders.favorites')}
              </span>
            </ChipToggle>
            <Button
              variant="secondary"
              size="md"
              icon={<FolderPlus size={13} strokeWidth={1.75} />}
              className={css({ ml: 'auto' })}
              onClick={() => {
                setFolderToEdit(null);
                setFolderModalMode('create');
              }}
            >
              {t('folders.newFolder')}
            </Button>
          </div>

          {loading && (
            <div className={centered}>
              <Spinner size={22} />
            </div>
          )}

          {!loading && error && <div className={emptyState}>{error}</div>}

          {!loading && !error && analyses.length === 0 && (
            <div className={emptyState}>
              <Search size={20} strokeWidth={1.5} />
              <span>
                {t('productSearch.emptyTitle')}
                <br />
                {t('productSearch.emptySubtitle')}
              </span>
            </div>
          )}

          {!loading && analyses.length > 0 && (
            <SearchHistoryFolders
              analyses={analyses}
              buildMeta={buildMeta}
              folders={folders}
              actions={actions}
              filterText={filterText}
              favoritesOnly={favoritesOnly}
              onOpen={(id) => navigate(`/analysis/products/${id}`)}
              onRenameFolder={(folder) => {
                setFolderToEdit(folder);
                setFolderModalMode('rename');
              }}
              onCreateSearch={(folderId) => {
                setCreateInFolderId(folderId);
                setIsModalOpen(true);
              }}
              onRenameSearch={(analysis) => setRenameTarget(analysis)}
              onViewSearch={(analysis) => setViewTarget(analysis)}
            />
          )}
        </div>
      </div>

      <SearchModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCreateInFolderId(null);
        }}
        onSubmit={handleSubmit}
        initialFolderId={createInFolderId}
        folders={folders}
      />

      {/* Renommage rapide (menu ⋯) */}
      <RenameSearchModal
        isOpen={renameTarget !== null}
        initialName={renameTarget?.name ?? ''}
        onClose={() => setRenameTarget(null)}
        onSubmit={async (name) => {
          if (renameTarget) await renameAnalysis(renameTarget.id, name);
        }}
      />

      {/* Voir les filtres (menu ⋯) — popup en lecture seule */}
      {viewTarget && (
        <SearchModal
          isOpen
          mode="edit"
          initialName={viewTarget.name}
          initialUrl={viewTarget.pageUrl}
          initialParsedFilter={viewTarget.parsedFilter}
          initialImageUrl={viewTarget.imageUrl ?? null}
          initialPlatform={viewTarget.platform}
          initialVestiaireFilter={viewTarget.vestiaireFilter ?? null}
          onClose={() => setViewTarget(null)}
          onSubmit={async ({ title }) => {
            await renameAnalysis(viewTarget.id, title);
          }}
        />
      )}

      <FolderFormModal
        isOpen={folderModalMode !== null}
        onClose={() => {
          setFolderModalMode(null);
          setFolderToEdit(null);
        }}
        onSubmit={handleFolderSubmit}
        mode={folderModalMode ?? 'create'}
        initialName={folderToEdit?.name}
      />
    </AppLayout>
  );
};
